// src/cmps/review/ReviewIndex.jsx
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchReviews } from '../../store/slices/review.slice'
import { fetchUsers } from '../../store/slices/user.slice'
import { ReviewList } from './ReviewList'
import { ReviewPopup } from './ReviewPopup'
import toast from 'react-hot-toast'
import { openNotRegisteredPopup } from '../../store/slices/ui.slice'
import { Button } from '../ui/elements'
import { Like, UnLike } from '../ui/icons/shared'

export function ReviewIndex({ animeId, loggedInUser }) {
  const dispatch = useDispatch()
  const reviews = useSelector((state) => state.reviewModule.reviews)
  const users = useSelector((state) => state.userModule.users)

  const [isPopupOpen, setIsPopupOpen] = useState(false)

  // סינון ביקורות לפי animeId (שהוא עכשיו anizone_id)
  const filteredReviews = useMemo(() => {
    return reviews.filter((review) => review.animeId === animeId)
  }, [reviews, animeId])

  const recommendedCount = useMemo(() => {
    return filteredReviews.filter((review) => review.recommended).length
  }, [filteredReviews])

  const notRecommendedCount = useMemo(() => {
    return filteredReviews.filter((review) => review.recommended === false)
      .length
  }, [filteredReviews])

  const relevantUserIds = useMemo(() => {
    const userIds = filteredReviews.map((review) => review.userId)
    return [...new Set(userIds)]
  }, [filteredReviews])

  useEffect(() => {
    if (animeId) {
      dispatch(fetchReviews({ animeId }))
    }
  }, [animeId, dispatch])

  useEffect(() => {
    if (relevantUserIds.length) {
      dispatch(fetchUsers({ userIds: relevantUserIds }))
    }
  }, [relevantUserIds, dispatch])

  // בדיקה האם המשתמש כבר כתב ביקורת לאותה אנימה
  const userAlreadyReviewed = useMemo(() => {
    if (!loggedInUser) return false
    return filteredReviews.some((review) => review.userId === loggedInUser._id)
  }, [filteredReviews, loggedInUser])

  // טיפול בלחיצה על כפתור "כתוב ביקורת"
  const handleWriteReview = () => {
    if (!loggedInUser) {
      // אם המשתמש לא מחובר, פותחים פופאפ או מציגים הודעה מתאימה
      toast.error('עליך להיות מחובר כדי לכתוב ביקורת.')
      dispatch(openNotRegisteredPopup())
      return
    }
    if (userAlreadyReviewed) {
      toast.error('כבר כתבת ביקורת לאנימה זו!')
    } else {
      setIsPopupOpen(true)
    }
  }

  return (
    <section className="container mx-auto mt-12">
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg">
            דירוגים <span>({filteredReviews.length})</span>
          </h3>
          <Button
            text="כתוב ביקורת"
            width={140}
            height={60}
            onClick={handleWriteReview}
            fontSize="text-lg"
            textColor="text-buttonSecondary1"
            bgColor="fill-accentLight"
            hoverBgColor="hover:scale-105 transform duration-300"
            aria-label="פתח טופס כתיבת ביקורת על האנימה"
            title="כתוב ביקורת על האנימה הזו"
          />
        </div>

        <div className="flex gap-6">
          <div className="flex gap-3 items-center">
            <Like />
            <h5 className="text-white text-lg">המליצו</h5>
            <span className="text-primary text-lg">{recommendedCount}</span>
          </div>
          <div className="w-[2px] h-7 bg-muted rotate-[20deg]"></div>
          <div className="flex gap-3 items-center">
            <UnLike />
            <h5 className="text-white text-lg">לא המליצו</h5>
            <span className="text-primary text-lg">{notRecommendedCount}</span>
          </div>
        </div>
      </div>

      <ReviewList
        reviews={filteredReviews}
        loggedInUser={loggedInUser}
        users={users}
      />

      {isPopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <ReviewPopup
            initialData={null}
            animeId={animeId}
            loggedInUser={loggedInUser}
            onClose={() => setIsPopupOpen(false)}
          />
        </div>
      )}
    </section>
  )
}
